import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {CAMPAIGN_APPLICATION_KEYS} from "@/api/campaignApplicationApi";
import {CHAT_KEYS} from "@/api/chatApi";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import SliderInputField from "@/components/Common/forms/SliderInputField";
import Modal from "@/components/Common/Modal";
import useChatParams from "@/hooks/useChatParams";
import {client} from "@/httpClient";
import {queryClient} from "@/queryClient";
import {extendCampaignModalDataAtom} from "@/store/jotaiAtoms";
import {useMutation} from "@tanstack/react-query";
import dayjs from "dayjs";
import {useAtom} from "jotai";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const MIN_DAYS = 1;
const MAX_DAYS = 30;

const ExtendCampaignModal: FC = () => {
  const {t} = useTranslation("campaign_active");
  const {influencerUserId} = useChatParams();
  const [extendCampaignModalData, setExtendCampaignModalData] = useAtom(extendCampaignModalDataAtom);
  const application = useMemo(() => extendCampaignModalData?.application, [extendCampaignModalData?.application]);
  const campaign = useMemo(() => extendCampaignModalData?.campaign!, [extendCampaignModalData?.campaign]);
  const mode = useMemo(() => (!!application ? "SINGLE_APPLICATION" : "ALL_APPLICATIONS"), [application]);
  const mutationKey = useMemo(
    () =>
      mode === "SINGLE_APPLICATION" && !!application?.id
        ? CAMPAIGN_APPLICATION_KEYS.mApplicationExtendCompleted(application?.id!)
        : CAMPAIGN_KEYS.mCampaignExtendCompleted(campaign.id!),
    [mode, application?.id, campaign.id],
  );
  const [days, setDays] = useState(10);

  const {mutate: extend, isPending} = useMutation({
    mutationKey: mutationKey,
    mutationFn: () => client.post(mutationKey[0], {days}),
    onSuccess: () => {
      [
        CAMPAIGN_KEYS.qCampaign(campaign.id!),
        CAMPAIGN_KEYS.qCampaignActiveApplications(campaign.id!),
        CHAT_KEYS.qChatMessageList({campaignId: campaign.id!, influencerUserId}),
      ].map(queryKey => queryClient.invalidateQueries({queryKey}));
    },
  });

  return (
    <Modal
      onClose={() => setExtendCampaignModalData(null)}
      isLoading={isPending}
      isConfirmDisabled={isPending}
      onConfirm={() => extend()}
      confirmButtonText={t("confirm_extension")}
      modalTitle={t(mode === "ALL_APPLICATIONS" ? "extend_campaign_for_everyone" : "extend_campaign")}
      backgroundClassName="max-w-[600px]">
      <div className="flex flex-col gap-5">
        <div className="text-neutral-black flex flex-col gap-2 text-sm">
          <span className="font-semibold">{t("active_phase_extension")}</span>
          <span>
            {t(
              mode === "ALL_APPLICATIONS"
                ? campaign.ugc
                  ? "for_all_applicants_ugc"
                  : "for_all_applicants"
                : "for_applicant",
              {name: application?.first_name},
            )}
          </span>
        </div>
        <SliderInputField value={days} onChange={value => setDays(+value)} min={1} max={30} />
        <div className="relative flex h-10 w-full items-center rounded-md bg-white">
          <input
            type="number"
            className="border-neutral-grey-light-dark text-neutral-black block h-12 w-20 rounded-[4px] border px-3 py-1 pl-12 align-middle text-base"
            maxLength={2}
            value={days}
            onChange={e => setDays(parseInt(e.target.value))}
            onBlur={() => {
              if (days < MIN_DAYS) {
                setDays(MIN_DAYS);
              } else if (days > MAX_DAYS) {
                setDays(MAX_DAYS);
              }
            }}
          />
          <span className="text-neutral-black ml-3 text-sm font-semibold">{t("days", {count: days})}</span>
          <div className="bg-pretty-pink-400 absolute left-1.5 size-8 shrink-0 rounded-md" />
        </div>
        <div className="text-neutral-black flex gap-5 text-sm">
          <div className="flex flex-col gap-1">
            <span className="font-semibold">{t("from")}</span>
            <span>
              {dayjs(mode === "SINGLE_APPLICATION" ? application?.completed : campaign.completed).format(
                "DD. MM. YYYY",
              )}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="font-semibold">{t("to")}</span>
            <span>
              {dayjs(mode === "SINGLE_APPLICATION" ? application?.completed : campaign.completed)
                .add(days, "day")
                .format("DD. MM. YYYY")}
            </span>
          </div>
        </div>
        {mode === "ALL_APPLICATIONS" && (
          <div className="border-warning-dark-yellow bg-warning-light-yellow text-warning-dark-yellow flex items-center gap-2 rounded-md border px-3 py-4">
            <WarningIcon className="size-4 shrink-0" />
            <span className="text-sm">
              {t(campaign.ugc ? "extend_campaign_for_everyone_warning_ugc" : "extend_campaign_for_everyone_warning")}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ExtendCampaignModal;
